import { useEffect, useCallback, useState } from "react";
import { RiCloseLine } from "react-icons/ri";
import Button from "../../Utility/Button/Button";
import Typography from "../../Utility/Typography";
import IssueDetail from "./IssueDetail";
import "../../Utility/Loader/loader.css";

/**
 * Full-screen overlay with the same {@link IssueDetail} content as the former full-page view.
 */
export default function WorkItemDetailModal({
  projectSlug,
  issueId,
  onClose,
  onSaved,
  /** Open parent epic (or other related item) in this modal without closing. */
  onOpenRelatedWorkItem,
  /** When save moves the work item to another project — update modal route context. */
  onProjectIdChange,
  /** Encoded in copied links for “Back to project” (`issues`, `boards`, …). */
  linkReturnTab = "issues",
}) {
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    setIsBootstrapping(true);
  }, [projectSlug, issueId]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const stopPanelClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  if (!projectSlug || !issueId) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="work-item-detail-modal-title"
    >
      <button
        type="button"
        aria-label="Close work item details"
        className="absolute inset-0 h-full w-full cursor-default border-0 bg-black/50 p-0"
        onClick={onClose}
      />
      <div
        className="relative z-[1] flex h-[min(92vh,900px)] max-h-[min(92vh,900px)] w-full max-w-[min(1200px,96vw)] flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={stopPanelClick}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-surface-secondary/60 px-4 py-3">
          <Typography
            id="work-item-detail-modal-title"
            variant="h5"
            component="h2"
            className="font-semibold text-text-primary"
          >
            Work item
          </Typography>
          <Button
            type="button"
            variant="menu"
            size="sm"
            onClick={onClose}
            aria-label="Close"
            className="!p-1 !text-text-secondary hover:!text-text-primary hover:!bg-surface-secondary/80"
          >
            <RiCloseLine className="h-6 w-6" aria-hidden />
          </Button>
        </div>
        <div className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <IssueDetail
            projectSlug={projectSlug}
            issueId={issueId}
            presentation="modal"
            linkReturnTab={linkReturnTab}
            onBootstrapLoadingChange={setIsBootstrapping}
            onWorkItemSaved={onSaved}
            onOpenRelatedWorkItem={onOpenRelatedWorkItem}
            onProjectIdChange={onProjectIdChange}
          />
          {isBootstrapping ? (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 bg-white"
              role="status"
              aria-live="polite"
              aria-busy="true"
              aria-label="Loading work item"
            >
              <div className="custom-spinner" aria-hidden />
              <Typography variant="text2" className="text-text-secondary">
                Loading work item…
              </Typography>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
